import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Grid,
} from "@mui/material";
import { tokens } from "@/tokens";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useCreateFirstSaleCaseMutation } from "@/services/first-sale";

interface CreateCaseModalProps {
  open: boolean;
  onClose: () => void;
  supplierId: string;
}

export const CreateCaseModal: React.FC<CreateCaseModalProps> = ({
  open,
  onClose,
  supplierId,
}) => {
  const [form, setForm] = useState({
    caseNumber: "",
    poNumber: "",
    middlemanName: "",
    factoryName: "",
    middlemanValue: "",
    factoryValue: "",
    dutyRate: "",
    htsCode: "",
    productDescription: "",
  });
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const [createCase, { isLoading }] = useCreateFirstSaleCaseMutation();

  const isTDAdmin = user?.userRole === roles.TDADMIN;
  const isTDAdminTCD = isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";

  const clientId = isTDAdminTCD
    ? selectedTenant?._id
    : selectedClient?._id
      ? selectedClient._id
      : (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT)
        ? user?._id
        : undefined;

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.caseNumber.trim() || !form.poNumber.trim()) return;

    try {
      const middlemanValue = form.middlemanValue ? parseFloat(form.middlemanValue) : undefined;
      const factoryValue = form.factoryValue ? parseFloat(form.factoryValue) : undefined;
      const dutyRate = form.dutyRate ? parseFloat(form.dutyRate) : undefined;

      await createCase({
        input: {
          ...(clientId && { clientId }),
          ...(isTDAdmin && selectedTenant && { tenantId: selectedTenant._id }),
          supplierId,
          caseNumber: form.caseNumber.trim(),
          poNumber: form.poNumber.trim(),
          middlemanName: form.middlemanName.trim() || undefined,
          factoryName: form.factoryName.trim() || undefined,
          middlemanValue,
          factoryValue,
          dutyRate,
          htsCode: form.htsCode.trim() || undefined,
          productDescription: form.productDescription.trim() || undefined,
        },
      }).unwrap();

      setForm({
        caseNumber: "",
        poNumber: "",
        middlemanName: "",
        factoryName: "",
        middlemanValue: "",
        factoryValue: "",
        dutyRate: "",
        htsCode: "",
        productDescription: "",
      });
      onClose();
    } catch (err) {
      console.error("Failed to create case:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create First Sale Case</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Case Number"
                value={form.caseNumber}
                onChange={handleChange("caseNumber")}
                fullWidth
                required
                placeholder="e.g., FS-CREST-001"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="PO Number"
                value={form.poNumber}
                onChange={handleChange("poNumber")}
                fullWidth
                required
                placeholder="e.g., 7480"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Middleman Name"
                value={form.middlemanName}
                onChange={handleChange("middlemanName")}
                fullWidth
                placeholder="Trading company name"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Factory Name"
                value={form.factoryName}
                onChange={handleChange("factoryName")}
                fullWidth
                placeholder="Manufacturer name"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Middleman Value (USD)"
                value={form.middlemanValue}
                onChange={handleChange("middlemanValue")}
                fullWidth
                type="number"
                placeholder="e.g., 30730.40"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Factory Value (USD)"
                value={form.factoryValue}
                onChange={handleChange("factoryValue")}
                fullWidth
                type="number"
                placeholder="e.g., 23074.80"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Duty Rate (%)"
                value={form.dutyRate}
                onChange={handleChange("dutyRate")}
                fullWidth
                type="number"
                placeholder="e.g., 25"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="HTS Code"
                value={form.htsCode}
                onChange={handleChange("htsCode")}
                fullWidth
                placeholder="e.g., 6302.60.0020"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Product Description"
                value={form.productDescription}
                onChange={handleChange("productDescription")}
                fullWidth
                placeholder="Brief product description"
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!form.caseNumber.trim() || !form.poNumber.trim() || isLoading}
          sx={{
            bgcolor: tokens.brand.accent,
            textTransform: "none",
            "&:hover": { bgcolor: tokens.brand.accent, opacity: 0.9 },
          }}
        >
          {isLoading ? "Creating..." : "Create Case"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
